import React, { Suspense, useMemo, useEffect, useCallback } from 'react';
import {
  setTag,
  ErrorBoundary,
  captureException,
  captureMessage,
} from '@sentry/react';
import * as configcat from 'configcat-js';
import type { User } from 'configcat-common';
import { SendErrorProvider, HandleError } from 'cf-app-shell';

import { safeJoin } from '@sentry/utils';
import { CenteredDotBounceLoading } from '../NavLoading';
import { AppShellErrorPage as ErrorPage } from '../ErrorPage';
import SidebarCallback from '../SidebarCallback';

// Keep a reference to original console.error to call when we've overwritten it
// eslint-disable-next-line no-console
const consoleError = console.error;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ConsoleErrorType = (message?: any, ...optionalParams: any[]) => void;
export interface FederationWrapperProps {
  pageName: string;
  userObject?: User;
  userName?: string;
  setTheme: SetThemeFn;
  configureNavigation: configureNavigationFn;
  portalJwt?: string;
  configcatProjectKey?: string;
  hideIntercom: boolean;
  children(
    setTheme: SetThemeFn,
    configureNavigation: configureNavigationFn,
    configureSidebar: ConfigureSidebarFn,
    featureFlags?: { getValue: GetFlagValue },
  ): JSX.Element;
}

const generateFeatureFlags = (
  clientSDK?: string,
  userObject?: User,
): FeatureFlags | undefined => {
  if (!clientSDK) return undefined;
  const client = configcat.getClient(
    clientSDK,
    configcat.PollingMode.LazyLoad,
    { cacheTimeToLiveSeconds: 60 * 15 },
  );

  return {
    getValue: async (key, defaultValue = false) => {
      const res = await client.getValueAsync(key, defaultValue, userObject);
      return res;
    },
    getAllFeatureFlags: async () => client.getAllValuesAsync(userObject),
  };
};

/**
 * Wrapper to provide better error and loading handling around federated modules
 */
const FederationWrapper = ({
  pageName,
  userName,
  userObject,
  setTheme,
  configureNavigation,
  portalJwt,
  configcatProjectKey,
  hideIntercom,
  children,
}: FederationWrapperProps): JSX.Element => {
  const featureFlags = useMemo(
    () => generateFeatureFlags(configcatProjectKey, userObject),
    [configcatProjectKey, userObject],
  );

  useEffect(() => {
    setTag('page', pageName);
  }, [pageName]);

  useEffect(() => {
    return () => {
      setTheme('');
      configureNavigation({ noSiteNav: true });
    };
  }, [setTheme, configureNavigation]);

  const handleError: HandleError = useCallback((...args) => {
    consoleError(...args);

    // Find the `Error` (adapted from https://github.com/getsentry/sentry-javascript/blob/develop/packages/integrations/src/captureconsole.ts)
    const error = args.find((arg) => arg instanceof Error);

    if (error) {
      captureException(error);
    } else {
      captureMessage(safeJoin(args, ' '), 'error');
    }
  }, []);

  useEffect(() => {
    // Override console error with handleError to also send errors to sentry
    window.console.error = handleError;

    return () => {
      window.console.error = consoleError as ConsoleErrorType;
    };
  }, [handleError]);

  return (
    <Suspense fallback={<CenteredDotBounceLoading />}>
      <SidebarCallback portalJwt={portalJwt} hideIntercom={hideIntercom}>
        {(configureSidebar: ConfigureSidebarFn) => (
          <ErrorBoundary
            fallback={({ error }) => (
              <ErrorPage
                errorCode={(error as CustomHttpError).statusCode}
                userName={userName}
              />
            )}
          >
            <SendErrorProvider value={handleError}>
              {children(
                setTheme,
                configureNavigation,
                configureSidebar,
                featureFlags ? { getValue: featureFlags?.getValue } : undefined,
              )}
            </SendErrorProvider>
          </ErrorBoundary>
        )}
      </SidebarCallback>
    </Suspense>
  );
};

export default FederationWrapper;
